import React from 'react';

import type { Text } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  FormField,
  FormHelp,
  Heading,
  IconCalendarOutline,
  Label,
  Textarea,
} from '@zillow/constellation';

import type { Section } from '../../../models';
import { FIRE_SAFETY } from '../../../constants';
import type { HTMLElementEvent } from '../../../models/Types';
import { CenteredHR } from '../../../../shared/components/Widgets/CenteredHR';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../constants/stringConstants';
import { disableFutureDates } from '../../../constants/functionConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function FireSafety({ section, setSectionValues }: Props) {
  function setFireExtinguisherLocationDescription(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_LOCATION]: event.currentTarget.value,
    });
  }

  function onFireExtinguisherServiceDateChange(date: Date): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_SERVICE_DATE]: date,
    });
  }

  function setEmergencyExitDescription(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.EMERGENCY_EXIT_LOCATION]: event.currentTarget.value,
    });
  }

  function onSmokeAlarmReplacedDateChange(date: Date): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.SMOKE_ALARM_REPLACED_DATE]: date,
    });
  }

  function onCarbonMonoxideAlarmReplacedDateChange(date: Date): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.CARBON_MONOXIDE_ALARM_REPLACED_DATE]: date,
    });
  }

  return (
    <>
      <Heading level={6}>What are the fire safety features details for the property?</Heading>
      <FormField
        marginBottom="sm"
        label={<Label>Describe the location of the fire extinguisher(s) on the tenant’s floor</Label>}
        control={
          <Textarea
            resize="vertical"
            onChange={setFireExtinguisherLocationDescription}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_LOCATION)}
          />
        }
      />
      <FormField
        label={<Label>Fire extinguisher service date</Label>}
        control={
          <AdornedInput
            input={
              <DatePickerInput
                placeholder={DATE_PICKER_PLACEHOLDER}
                value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_SERVICE_DATE)}
                onChange={onFireExtinguisherServiceDateChange}
                datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableFutureDates} />}
              />
            }
            rightAdornment={
              <Adornment>
                <IconCalendarOutline />
              </Adornment>
            }
          />
        }
      />
      <CenteredHR />
      <FormField
        label={<Label>Describe the location of the emergency exit(s) on the tenant’s floor</Label>}
        control={
          <Textarea
            resize="vertical"
            onChange={setEmergencyExitDescription}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.EMERGENCY_EXIT_LOCATION)}
          />
        }
        description={<FormHelp>Emergency exits shall remain unobstructed at all times</FormHelp>}
      />
      <CenteredHR />
      <FormField
        marginBottom="sm"
        label={<Label>Smoke alarm(s) last replaced date</Label>}
        control={
          <AdornedInput
            input={
              <DatePickerInput
                placeholder={DATE_PICKER_PLACEHOLDER}
                value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.SMOKE_ALARM_REPLACED_DATE)}
                onChange={onSmokeAlarmReplacedDateChange}
                datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableFutureDates} />}
              />
            }
            rightAdornment={
              <Adornment>
                <IconCalendarOutline />
              </Adornment>
            }
          />
        }
      />
      <FormField
        label={<Label>Carbon monoxide alarm(s) last replaced date</Label>}
        control={
          <AdornedInput
            input={
              <DatePickerInput
                placeholder={DATE_PICKER_PLACEHOLDER}
                value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.CARBON_MONOXIDE_ALARM_REPLACED_DATE)}
                onChange={onCarbonMonoxideAlarmReplacedDateChange}
                datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableFutureDates} />}
              />
            }
            rightAdornment={
              <Adornment>
                <IconCalendarOutline />
              </Adornment>
            }
          />
        }
      />
    </>
  );
}

FireSafety.description = 'Asks about fire safety features within a building';

export default FireSafety;
